import {
  AuthService,
  EncryptedContactIdRouteInterceptor,
  LoginEmailService,
  LoginFormType,
  LoginWithUserNamePasswordStrategy,
  QUERY_PARAM_LOGIN_FORM_TYPE,
  RefusedMultiFactorAuthenticationEnrollment,
  RefusedMultiFactorAuthenticationVerification,
  SendPasswordlessLoginEmailUserError,
  SetLoginFormType,
} from '@aiwyn/auth';
import { AssetUriService, ConfigService, ConfigState, LaunchDarklyFlag, LaunchDarklyService, LaunchDarklyState } from '@aiwyn/config';
import {
  canShowValidationErrors,
  EMAIL_REGEX,
  FunctionNames,
  ignoreFalsy,
  ignoreNullish,
  LoadableHelper,
  LoadableStatus,
  Logger,
  SnackbarService,
  SnackbarType,
} from '@aiwyn/shared';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, Output, signal, ViewEncapsulation } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { BehaviorSubject, EMPTY, exhaustMap, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ROUTE_TOKEN_LOGIN, ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_REQUEST_PASSWORD_RESET } from '../../../../routing-constants';
import { CustomAnimation } from '../custom-animation';

const EMAIL_LOCK_KEY = 'emailLockState';

export interface LoginEmailLock {
  email: string,
  contactId: string,
  isRegistered: boolean
}

@Component({
  selector: 'app-login-component',
  templateUrl: './app-login.component.html',
  styleUrls: ['./app-login.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: CustomAnimation,
  standalone: false,
})
export class AppLoginComponent implements OnDestroy {
  @Input() logoUrl: string;
  @Input() appearance: MatFormFieldAppearance;
  @Input() resetPasswordEnabled = true;
  @Input() guestEnabled = true;

  @Input() tosUrl: string;
  @Input() privacyPolicyUrl: string;

  // i18n
  @Input() titleText = 'Log in to your Aacount';
  @Input() rememberMeText = 'Remember me';
  @Input() loginButtonText = 'Log in';
  @Input() orLabelText = 'OR';
  @Input() forgotPasswordText = 'Forgot password?';
  @Input() dontHaveAnAccountText = 'Not signed up?';
  @Input() createAccountButtonText = 'Create an account';

  // i18n email
  @Input() emailText = 'Email *';
  @Input() emailErrorRequiredText = 'Email is required';
  @Input() emailErrorPatternText = 'Please enter a valid email address';

  // i18n password
  @Input() passwordText = 'Password *';
  @Input() passwordErrorRequiredText = 'Password is required';

  // Events
  @Output() onCreateAccountRequested: EventEmitter<void> = new EventEmitter<void>();
  @Output() onResetPasswordRequested: EventEmitter<void> = new EventEmitter<void>();

  readonly LoginFormTypes = LoginFormType;
  readonly canShowValidationErrors = canShowValidationErrors;

  // dialogRef: MatDialogRef<LegalityDialogComponent>;
  private _unsubscribe$: Subject<void> = new Subject<void>();
  loginForm = new UntypedFormGroup({ email: new UntypedFormControl('', [Validators.required, Validators.email]) });
  authenticationError = signal(undefined);
  authenticationUserError = signal(false);
  resetAuthenticationError$ = toObservable(this.authenticationError).pipe(
    ignoreFalsy(),
    exhaustMap(_ => this.loginForm.valueChanges.pipe(take(1))),
    tap(() => this.authenticationError.set(undefined)),
    takeUntil(this._unsubscribe$),
  );

  private originalDecodedRedirectUrl: string;
  protected lockedEmailIsValid = true;

  loginFormType$ = new BehaviorSubject<LoginFormType>(undefined);
  initializeLoginFormOnTypeChange$ = this.loginFormType$.pipe(
    ignoreNullish(),
    tap(formType => this.initializeForm(formType)),
    shareReplay(1),
    takeUntil(this._unsubscribe$)
  );
  shouldShowPasswordInput$ = this.loginFormType$.pipe(
    map(loginFormType => [LoginFormType.USERNAME_PASSWORD, LoginFormType.ORIGINAL_USERNAME_PASSWORD].includes(loginFormType)),
  );

  customerName$ = this.configService.configValuesLoaded$.pipe(
    switchMap(_ => this.store.select(ConfigState.getCustomerName)),
  );

  setLoginFormTypeFromQueryParam$ = this.route.queryParams.pipe(
    take(1),
    map<Params, LoginFormType>(params => params[QUERY_PARAM_LOGIN_FORM_TYPE]),
  ).pipe(
    filter(loginFormType => Object.values(LoginFormType).includes(loginFormType)),
    tap(loginFormType => this.loginFormType$.next(loginFormType)),
  );

  setQueryParamOnLoginFormTypeChange$ = this.loginFormType$.pipe(
    ignoreNullish(),
    tap(loginFormType => {
      this.store.dispatch(new SetLoginFormType(loginFormType));
      // Be sure to preserve the existing Query Parameters so that we don't loose the redirect URL, for example
      this.router.navigate([], { queryParams: { [QUERY_PARAM_LOGIN_FORM_TYPE]: loginFormType }, queryParamsHandling: 'merge' });
    }),
    takeUntil(this._unsubscribe$),
  );

  isFeatureFlagsLoaded$ = this.store.select(LaunchDarklyState.getStatus).pipe(
    map(status => {
      return status === LoadableStatus.LOADED;
    }),
    takeUntil(this._unsubscribe$),
  );

  enableMagicLinkExperience$ = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_PASSWORDLESS_LOGIN);

  toggleLoginFormType$ = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_PASSWORDLESS_LOGIN).pipe(
    distinctUntilChanged(),
    tap(isPasswordlessEnabled => {
      /**
       * There is a chance we were directed here from another place in the application that wants to use Username and Password,
       * so don't override it based on the Feature Flag.
       * TODO: Shouldn't we allow setting back to original username and password when flag is turned off?
       */
      if (this.loginFormType$.value !== LoginFormType.USERNAME_PASSWORD) {
        this.loginFormType$.next(isPasswordlessEnabled ? LoginFormType.EMAIL_LINK : LoginFormType.ORIGINAL_USERNAME_PASSWORD);
      }
    }),
    takeUntil(this._unsubscribe$),
  );

  isSignInProcessing$ = new BehaviorSubject<boolean>(false);

  constructor(
    private router: Router,
    public dialog: MatDialog,
    private launchDarklyService: LaunchDarklyService,
    public configService: ConfigService,
    public route: ActivatedRoute,
    public assetUriService: AssetUriService,
    public store: Store,
    private readonly logger: Logger,
    private readonly authService: AuthService,
    private readonly snackbarService: SnackbarService,
    private readonly usernameAndPasswordLogin: LoginWithUserNamePasswordStrategy,
    private readonly encryptedContactIdService: EncryptedContactIdRouteInterceptor,
    private readonly loginEmailService: LoginEmailService,
  ) {
    this.encryptedContactIdService.getDecryptedContactId().pipe(
      filter(({ status }) => LoadableHelper.isLoaded(status, true)),
      tap(({ data: decryptedData }) => {
        if (decryptedData) {
          this.loginForm.get('email').setValue(decryptedData.email);
          this.loginForm.get('email').disable();

          // SetTimeout necessary to force angular to actually redirect inside the constructor
          setTimeout(() => {
            if (!decryptedData?.isRegistered && this.loginFormType$.value === LoginFormType.ORIGINAL_USERNAME_PASSWORD) {
              this.router.navigate(['register']);
            }
          }, 0);
        }
      }),
      take(1),
    ).subscribe();
    this.setLoginFormTypeFromQueryParam$.subscribe();
    this.setQueryParamOnLoginFormTypeChange$.subscribe();
    this.toggleLoginFormType$.subscribe();
    this.initializeLoginFormOnTypeChange$.subscribe();
    this.resetAuthenticationError$.subscribe();
  }

  private initializeForm(loginFormType: LoginFormType): void {
    // Remove this so that adding it doesn't potentially make it add duplicates...
    this.loginForm.removeControl('password');
    if (loginFormType !== LoginFormType.EMAIL_LINK) {
      this.loginForm.addControl('password', new UntypedFormControl(null, [Validators.required]));
    }

    const emailLockStateStr = localStorage.getItem(EMAIL_LOCK_KEY);
    if (emailLockStateStr && emailLockStateStr !== 'null') {
      const emailLockState: LoginEmailLock = JSON.parse(emailLockStateStr);
      this.loginForm.get('email').setValue(emailLockState?.email ?? '');
    }

    this.loginForm.markAsPristine();
    this.loginForm.markAsUntouched();
  }

  enableSignInWithPassword(): void {
    this.loginFormType$.next(LoginFormType.USERNAME_PASSWORD);
  }

  getHelpSigningIn(): void {
    // Reset the login type, so if we end up coming back to this page, we essentially start from scratch
    this.store.dispatch(new SetLoginFormType(null));
    const email = this.loginForm.controls.email.value;
    this.router.navigate([ROUTE_TOKEN_LOGIN, 'getHelp'], { queryParams: { email } });
  }

  getClientKeyForAnonymousUser(): void {
    // Reset the login type, so if we end up coming back to this page, we essentially start from scratch
    this.store.dispatch(new SetLoginFormType(null));
    this.router.navigateByUrl('clientkey');
  }

  signInWithUsernameAndPassword(): void {
    if (!this.validate('signInWithUsernameAndPassword')) {
      this.logger.warn('AUTH : AppLoginComponent : signInWithUsernameAndPassword : invalid form');
      return;
    }
    this.logger.info('AUTH : AppLoginComponent : signInWithUsernameAndPassword : proceeding with sign in');

    this.isSignInProcessing$.next(true);
    this.authenticationError.set(undefined);
    // this.authService.startedLoginProcess = true;
    // this.authService.authEmail = this.loginForm.controls.email.value;
    this.authService.logIn(
      this.usernameAndPasswordLogin,
      {
        username: this.loginForm.get('email').value,
        password: this.loginForm.get('password').value
      }
    ).pipe(
      switchMap(_ => this.router.navigate([ROUTE_TOKEN_MAIN_BASE])),
      catchError(error => {
        if (error instanceof RefusedMultiFactorAuthenticationVerification) {
          this.authenticationError.set('Multi-factor authentication is required to access your account');
        } else if (error instanceof RefusedMultiFactorAuthenticationEnrollment) {
          this.authenticationError.set('Multi-factor enrollment is required to access your account');
        } else if (error instanceof HttpErrorResponse && error.status === 0) {
          this.authenticationError.set('Unable to connect to Aiwyn. Please check your network connection or contact your IT team if the issue persists.');
        } else {
          this.authenticationError.set('Invalid email address or password');
        }
        this.isSignInProcessing$.next(false);
        throw error;
      }),
      take(1),
      finalize(() => this.isSignInProcessing$.next(false)),
    ).subscribe();
  }

  signInWithEmailLink(): void {
    if (!this.validate('signInWithEmailLink')) {
      return;
    }

    this.isSignInProcessing$.next(true);
    const authEmail = this.loginForm.controls.email.value;

    this.loginEmailService.sendPasswordlessLoginEmail(authEmail).pipe(
      take(1),
      catchError(error => {
        // Error is the fault of the user, show bad input message
        if (error instanceof SendPasswordlessLoginEmailUserError) {
          this.authenticationUserError.set(true);
          return EMPTY;
        }

        // Generic error message for anything else
        this.snackbarService.open('There was a problem sending the email. Please try again.', SnackbarType.WARNING);
        throw error;
      }),
      finalize(() => this.isSignInProcessing$.next(false)),
    ).subscribe();
  }

   validate(callingFunction: FunctionNames<AppLoginComponent>): boolean {
    this.authenticationUserError.set(false);
    this.lockedEmailIsValid = true;
    this.loginForm.markAllAsTouched();
    if (this.loginForm.invalid) {
      this.logger.debug(`AppLoginComponent : ${callingFunction} : Form is not valid`);
    }

    // There can be a strange case where we disable the email control, and the email isn't valid
    // In this case, the form is neither valid or invalid, so we need to check for whether the email
    // is valid or not
    if (!this.loginForm.invalid && !this.loginForm.valid) {
      const validatorResults = Validators.email(this.loginForm.controls.email);
      if (validatorResults) {
        this.lockedEmailIsValid = false;
        this.logger.debug(`AppLoginComponent : ${callingFunction} : Email is not valid`);
        return false;
      }
    }

    // If every control in a form is disabled, the form's status is disabled, which results in .valid = false.
    // The second OR condition checks the one case where the form is disabled but still valid.
    return this.loginForm.valid
      || (this.loginFormType$.value === LoginFormType.EMAIL_LINK && EMAIL_REGEX.test(this.loginForm.value.email));
  }

  changePassword(): void {
    this.router.navigateByUrl(ROUTE_TOKEN_REQUEST_PASSWORD_RESET);
  }

  showGuestLogin(): boolean {
    return this.guestEnabled && !this.configService.relaxedModeEnabled && !this.configService.bypassClientKey;
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
